import { useRef, type ChangeEvent } from 'react';
import { Button, Stack } from '@mui/material';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { showToast } from 'Toast';
import { ALLOWED_IMAGE_TYPES, validateImageFiles } from './validateImageFiles';

// Long enough to read a list of file names.
const ERROR_TOAST_MS = 8000;

type Props = {
    // True while the upload is in flight. The owner runs the request, so it owns this flag.
    uploading: boolean;
    onUpload: (files: File[]) => void;

    // Absent when the item has no image, which is also when there is nothing to remove.
    onRemove?: () => void;
};

/*
    The React version of the Angular content-item-image-upload directive, for the course editor. The
    Angular directive stays where it is for the playlist editor, the lesson SEO form, the global
    metadata admin and the two lesson frame image editors.
*/
export default function ContentItemImageUpload({ uploading, onUpload, onRemove }: Props) {
    const inputRef = useRef<HTMLInputElement>(null);

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        const files = Array.from(event.target.files || []);

        // Clear the input so picking the same file twice in a row still fires a change event.
        event.target.value = '';

        if (files.length === 0) return;

        const errors = validateImageFiles(files);
        if (errors.length > 0) {
            showToast({
                message: 'Invalid Image',
                description: errors.join(' '),
                type: 'danger',
                timeout: ERROR_TOAST_MS,
            });
            return;
        }

        onUpload(files);
    }

    if (uploading) return <FrontRoyalSpinner className="inline" />;

    return (
        <Stack direction="row" spacing={1}>
            <Button variant="contained" color="primary" onClick={() => inputRef.current?.click()}>
                Upload Image
            </Button>

            {onRemove && (
                <Button name="remove" variant="contained" color="secondary" onClick={onRemove}>
                    Delete
                </Button>
            )}

            {/* One file. Both callers keep the first asset the upload returns and drop the rest,
                because a course and a metadata record each have one image. The Angular directive
                set ngf-multiple, so it sent every picked file to S3 and then used one. */}
            <input
                ref={inputRef}
                type="file"
                accept={ALLOWED_IMAGE_TYPES.join(',')}
                onChange={handleChange}
                style={{ display: 'none' }}
            />
        </Stack>
    );
}
